import { Combobox } from '@base-ui/react/combobox';
import { useEffect, useId, useRef, useState } from 'react';

import type { WalletRecipientView } from '@/api/address-book-bff/types';
import UserIcon from '@/assets/svg/ui-elements/user-outline.svg';
import { Helper, Label, theme } from '@/design-system';

import {
  Field,
  Input,
  LeadingIcon,
  List,
  Popup,
  Positioner,
  Root,
} from './send-recipient-combobox.styled';
import { SendRecipientOption } from './send-recipient-option';
import { SendRecipientSelected } from './send-recipient-selected';
import {
  getSendRecipientStatusCopy,
  SendRecipientLiveStatus,
  SendRecipientPopupStatus,
} from './send-recipient-status';

export interface SendRecipientComboboxProps {
  inputValue: string;
  selectedRecipient: WalletRecipientView | null;
  recipients: WalletRecipientView[];
  isSearching: boolean;
  showEmpty: boolean;
  showSearchError: boolean;
  invalid: boolean;
  helperText: string;
  disabled?: boolean;
  onInputChange(_value: string): void;
  onSelect(_recipient: WalletRecipientView): void;
  onEdit(): void;
  onClear(): void;
  onBlur(): void;
}

export const SendRecipientCombobox = ({
  inputValue,
  selectedRecipient,
  recipients,
  isSearching,
  showEmpty,
  showSearchError,
  invalid,
  helperText,
  disabled = false,
  onInputChange,
  onSelect,
  onEdit,
  onClear,
  onBlur,
}: SendRecipientComboboxProps) => {
  const inputId = useId();
  const helperId = useId();
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const focusInputRef = useRef<'clear' | 'edit' | null>(null);
  const visibleStatus = getSendRecipientStatusCopy({ isSearching, showEmpty, showSearchError });
  const hasSettledResults =
    selectedRecipient === null && inputValue.trim().length >= 2 && recipients.length > 0;
  const liveStatus =
    visibleStatus ||
    (hasSettledResults
      ? `${recipients.length} ${recipients.length === 1 ? 'contact' : 'contacts'} found`
      : '');
  const canShowPopup =
    selectedRecipient === null &&
    inputValue.trim().length >= 2 &&
    (recipients.length > 0 || visibleStatus.length > 0);

  useEffect(() => {
    if (selectedRecipient !== null || focusInputRef.current === null) return;

    focusInputRef.current = null;
    inputRef.current?.focus();
  }, [selectedRecipient]);

  useEffect(() => {
    if (canShowPopup && inputRef.current === document.activeElement) setOpen(true);
  }, [canShowPopup]);

  const handleEdit = () => {
    focusInputRef.current = 'edit';
    setOpen(true);
    onEdit();
  };

  const handleClear = () => {
    focusInputRef.current = 'clear';
    onClear();
  };

  return (
    <Root>
      <Label htmlFor={inputId} disabled={disabled}>
        Send to
      </Label>
      <Combobox.Root<WalletRecipientView>
        items={recipients}
        filter={null}
        inputValue={inputValue}
        onInputValueChange={(nextValue, eventDetails) => {
          if (eventDetails.reason === 'input-clear') {
            eventDetails.cancel();
            return;
          }
          if (eventDetails.reason === 'input-change') {
            onInputChange(nextValue);
          }
        }}
        value={selectedRecipient}
        onValueChange={(nextRecipient) => {
          if (nextRecipient) onSelect(nextRecipient);
        }}
        itemToStringLabel={(item) => item.wallet.address}
        isItemEqualToValue={(item, value) => item.userId === value.userId}
        open={open && canShowPopup}
        onOpenChange={(nextOpen) => setOpen(nextOpen)}
        disabled={disabled}
      >
        {selectedRecipient ? (
          <SendRecipientSelected
            inputId={inputId}
            helperId={helperId}
            selectedRecipient={selectedRecipient}
            invalid={invalid}
            helperText={helperText}
            disabled={disabled}
            onEdit={handleEdit}
            onClear={handleClear}
          />
        ) : (
          <Field data-slot="send-recipient-field" $invalid={invalid} $disabled={disabled}>
            <LeadingIcon aria-hidden="true">
              <UserIcon />
            </LeadingIcon>
            <Input
              ref={inputRef}
              id={inputId}
              name="recipient"
              disabled={disabled}
              onBlur={onBlur}
              aria-invalid={invalid}
              aria-describedby={helperText ? helperId : undefined}
              placeholder="Enter Sui address, name or email"
              autoComplete="off"
              spellCheck={false}
            />
          </Field>
        )}

        <SendRecipientLiveStatus message={liveStatus} />

        <Combobox.Portal>
          <Positioner
            data-slot="send-recipient-positioner"
            sideOffset={theme.spacing[8]}
            collisionPadding={theme.spacing[8]}
            align="start"
          >
            <Popup data-slot="send-recipient-popup">
              <SendRecipientPopupStatus isSearching={isSearching} message={visibleStatus} />
              {recipients.length > 0 ? (
                <List>
                  {(recipient: WalletRecipientView) => (
                    <SendRecipientOption key={recipient.userId} recipient={recipient} />
                  )}
                </List>
              ) : null}
            </Popup>
          </Positioner>
        </Combobox.Portal>
      </Combobox.Root>
      {helperText ? (
        <div id={helperId}>
          <Helper state={invalid ? 'destructive' : 'default'} label={helperText} />
        </div>
      ) : null}
    </Root>
  );
};
